import { useState } from "react";
import useTranslation from "../../client/hooks/useTranslation";
import {
  CardSectionHeader,
  useIsDark,
} from "../../client/components/ui/card-section-header";
import { Input } from "../../client/components/ui/input";
import { Checkbox } from "../../client/components/ui/checkbox";
import { Button } from "../../client/components/ui/button";
import type {
  ComparatorFilters as Filters,
  FirmVM,
  DrawdownType,
  DailyLossFilter,
  RatingBand,
} from "../lib/types";
import { DRAWDOWN_TYPES } from "../lib/types";

interface FiltersProps {
  filters: Filters;
  firms: FirmVM[];
  availableSizes: number[];
  availablePlatforms: string[];
  onChange: (next: Filters) => void;
  onReset: () => void;
}

// "No limit" ceiling for the max-price slider. When the slider sits at the
// ceiling, maxPrice is stored as null (no upper bound).
const PRICE_CEILING = 1000;

function FilterGroup({
  label,
  isDark,
  children,
}: {
  label: string;
  isDark: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <CardSectionHeader label={label} isDark={isDark} compact />
      <div className="p-3 space-y-2">{children}</div>
    </div>
  );
}

const DAILY_OPTIONS: DailyLossFilter[] = ["all", "none", "defined"];
const RATING_OPTIONS: RatingBand[] = ["all", "high", "mid", "low"];

export default function ComparatorFilters({
  filters,
  firms,
  availableSizes,
  availablePlatforms,
  onChange,
  onReset,
}: FiltersProps) {
  const t = useTranslation();
  const isDark = useIsDark();
  const [showAllFirms, setShowAllFirms] = useState(false);

  // Toggle a value inside one of the array-valued filter fields.
  function toggleInArray<
    K extends "firmSlugs" | "sizesK" | "accountTypes" | "drawdownTypes" | "platforms",
  >(key: K, value: Filters[K][number]) {
    const current = filters[key] as Array<Filters[K][number]>;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...filters, [key]: next });
  }

  const drawdownLabels: Record<DrawdownType, string> = {
    eod: t.comparator.ddEod,
    eot: t.comparator.ddEot,
    trailing: t.comparator.ddTrailing,
    static: t.comparator.ddStatic,
  };
  const dailyLabels: Record<DailyLossFilter, string> = {
    all: t.comparator.dailyAll,
    none: t.comparator.dailyNone,
    defined: t.comparator.dailyDefined,
  };
  const ratingLabels: Record<RatingBand, string> = {
    all: t.comparator.ratingAll,
    high: t.comparator.ratingHigh,
    mid: t.comparator.ratingMid,
    low: t.comparator.ratingLow,
  };

  const visibleFirms = showAllFirms ? firms : firms.slice(0, 8);
  const sliderValue = filters.maxPrice ?? PRICE_CEILING;

  return (
    <div className="space-y-4">
      {/* Search */}
      <FilterGroup label={t.comparator.searchLabel} isDark={isDark}>
        <Input
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder={t.comparator.searchPlaceholder}
        />
      </FilterGroup>

      {/* Prop firm */}
      <FilterGroup label={t.comparator.firmLabel} isDark={isDark}>
        <div className="space-y-2">
          {visibleFirms.map((firm) => (
            <label
              key={firm.slug}
              className="flex items-center gap-2 text-sm text-foreground cursor-pointer"
            >
              <Checkbox
                checked={filters.firmSlugs.includes(firm.slug)}
                onCheckedChange={() => toggleInArray("firmSlugs", firm.slug)}
              />
              {firm.name}
            </label>
          ))}
        </div>
        {firms.length > 8 && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-1 h-auto px-0 text-primary"
            onClick={() => setShowAllFirms((v) => !v)}
          >
            {showAllFirms ? t.comparator.showLess : t.comparator.showAll}
          </Button>
        )}
      </FilterGroup>

      {/* Size */}
      <FilterGroup label={t.comparator.sizeLabel} isDark={isDark}>
        <div className="flex flex-wrap gap-2">
          {availableSizes.map((size) => {
            const active = filters.sizesK.includes(size);
            return (
              <button
                key={size}
                type="button"
                onClick={() => toggleInArray("sizesK", size)}
                className={`rounded-md px-2.5 py-1 text-xs font-mono transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-accent"
                }`}
              >
                {size}k
              </button>
            );
          })}
        </div>
      </FilterGroup>

      {/* Account type */}
      <FilterGroup label={t.comparator.accountTypeLabel} isDark={isDark}>
        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
          <Checkbox
            checked={filters.accountTypes.includes("eval")}
            onCheckedChange={() => toggleInArray("accountTypes", "eval")}
          />
          {t.comparator.typeEval}
        </label>
        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
          <Checkbox
            checked={filters.accountTypes.includes("direct")}
            onCheckedChange={() => toggleInArray("accountTypes", "direct")}
          />
          {t.comparator.typeDirect}
        </label>
      </FilterGroup>

      {/* Drawdown type */}
      <FilterGroup label={t.comparator.drawdownTypeLabel} isDark={isDark}>
        {DRAWDOWN_TYPES.map((dd) => (
          <label
            key={dd}
            className="flex items-center gap-2 text-sm text-foreground cursor-pointer"
          >
            <Checkbox
              checked={filters.drawdownTypes.includes(dd)}
              onCheckedChange={() => toggleInArray("drawdownTypes", dd)}
            />
            {drawdownLabels[dd]}
          </label>
        ))}
      </FilterGroup>

      {/* Platform */}
      <FilterGroup label={t.comparator.platformLabel} isDark={isDark}>
        {availablePlatforms.map((p) => (
          <label
            key={p}
            className="flex items-center gap-2 text-sm text-foreground cursor-pointer"
          >
            <Checkbox
              checked={filters.platforms.includes(p)}
              onCheckedChange={() => toggleInArray("platforms", p)}
            />
            {p}
          </label>
        ))}
      </FilterGroup>

      {/* Daily loss */}
      <FilterGroup label={t.comparator.dailyLossLabel} isDark={isDark}>
        <div className="flex flex-wrap gap-2">
          {DAILY_OPTIONS.map((opt) => {
            const active = filters.dailyLoss === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange({ ...filters, dailyLoss: opt })}
                className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-accent"
                }`}
              >
                {dailyLabels[opt]}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      {/* Rating */}
      <FilterGroup label={t.comparator.ratingLabel} isDark={isDark}>
        <div className="flex flex-wrap gap-2">
          {RATING_OPTIONS.map((opt) => {
            const active = filters.ratingBand === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange({ ...filters, ratingBand: opt })}
                className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-accent"
                }`}
              >
                {ratingLabels[opt]}
              </button>
            );
          })}
        </div>
      </FilterGroup>

      {/* Max price */}
      <FilterGroup label={t.comparator.maxPriceLabel} isDark={isDark}>
        <input
          type="range"
          min={0}
          max={PRICE_CEILING}
          step={10}
          value={sliderValue}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange({ ...filters, maxPrice: v >= PRICE_CEILING ? null : v });
          }}
          className="w-full accent-primary"
        />
        <div className="text-sm font-mono text-foreground">
          {filters.maxPrice == null
            ? `$${PRICE_CEILING.toLocaleString("en-US")}+`
            : `$${filters.maxPrice.toLocaleString("en-US")}`}
        </div>
      </FilterGroup>

      <Button variant="ghost" onClick={onReset} className="w-full">
        {t.comparator.resetFilters}
      </Button>
    </div>
  );
}
